import React, { FC, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Modal, Table, Tabs } from "antd"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import Styles from "./styles.module.css"
import { accountNativeGameBuffPlayColumns } from "./columns"
import { isExpired } from "../helpers"
import { GamePlatformType, GameTask } from "../store/account-games-BuffPlay.store"
import { AccountDailyMissionsSection } from "./account-daily-missions-section"
import { AccountBonusSpendSection } from "./account-bonus-spend-section"

type Props = {
  selectedGame: any | null,
  setSelectedGame: (e: any) => void
}

const PLATFORM_LABELS: Record<GamePlatformType, string> = {
  ANDROID: "Android",
  IOS: "iOS",
}

const ALL_PLATFORMS: GamePlatformType[] = [GamePlatformType.ANDROID, GamePlatformType.IOS]

export const AccountNativeGameBuffPlay: FC<Props> = observer(({ selectedGame, setSelectedGame }) => {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(false)
  const { dataStore: { accountGamesBuffPlayStore } } = useStore()

  // The admin picks the platform context they are operating on. We default to
  // the first platform the game has configured (typically ANDROID for legacy
  // games) so single-platform games keep working without an explicit choice.
  const availablePlatforms = useMemo<GamePlatformType[]>(() => {
    const fromGame = (selectedGame?.platforms ?? [])
      .map((p: any) => p?.platform)
      .filter((p: any): p is GamePlatformType => p === GamePlatformType.ANDROID || p === GamePlatformType.IOS)

    return fromGame.length > 0 ? fromGame : ALL_PLATFORMS
  }, [selectedGame?.platforms])

  const [activePlatform, setActivePlatform] = useState<GamePlatformType>(
    () => availablePlatforms[0] ?? GamePlatformType.ANDROID,
  )

  const tasksForPlatform = useMemo<GameTask[]>(() => {
    const tasks: GameTask[] = selectedGame?.tasks ?? []

    return tasks.filter(task => task.platform === activePlatform)
  }, [selectedGame?.tasks, activePlatform])

  const onInstallGame = async() => {
    if (!id) return
    try {
      await accountGamesBuffPlayStore.installGameBuffPlay({
        accountId: id,
        gameId: selectedGame.id,
        platform: activePlatform,
      })
      setSelectedGame(prev => ({
        ...prev,
        isInstalled: true,
      }))
      notifications.success("Success")
    } catch (err: any) {
      notifications.error(err?.message || err)
    }
  }

  const onResetGame = async() => {
    if (!id) return
    try {
      await accountGamesBuffPlayStore.resetGameTasksBuffPlay({
        accountId: id,
        gameId: selectedGame.id,
        platform: activePlatform,
      })

      // Only flip `completed=false` on tasks for the active platform — the
      // other platform's progress isn't touched server-side.
      setSelectedGame(prev => ({
        ...prev,
        tasks: prev.tasks.map((t: GameTask) => (
          t.platform === activePlatform ? { ...t, completed: false } : t
        )),
      }))
      notifications.success("Success")
    } catch (err: any) {
      notifications.error(err?.message || err)
    }
  }

  const onCompleteGame = async() => {
    if (!id) return
    setLoading(true)
    try {
      for (const task of tasksForPlatform) {
        if (!task.completed) {
          await accountGamesBuffPlayStore.completeGameTask({
            accountId: id,
            gameId: selectedGame.id,
            taskId: task.id,
            platform: activePlatform,
            reward: task.reward,
            rewardUsd: task.rewardUsd,
            taskName: task.name,
            taskType: task.type,
          })
        }
      }
      setSelectedGame(prev => ({
        ...prev,
        tasks: prev.tasks.map((t: GameTask) => (
          t.platform === activePlatform ? { ...t, completed: true } : t
        )),
      }))
      notifications.success("Success")
    } catch (err: any) {
      notifications.error(err?.message || err)
    } finally {
      setLoading(false)
    }
  }

  const onCreateRewardTask = async(task: GameTask, status?: string) => {
    if (!id) return
    try {
      await accountGamesBuffPlayStore.completeGameTask({
        accountId: id,
        gameId: selectedGame.id,
        taskId: task.id,
        platform: activePlatform,
        reward: task.reward,
        rewardUsd: task.rewardUsd,
        taskName: task.name,
        taskType: task.type,
        status,
      })
      // PENDING leaves the task incomplete; a claim-style status marks it done.
      const isPending = status === "PENDING"
      setSelectedGame(prev => ({
        ...prev,
        tasks: prev.tasks.map((t: GameTask) => (
          t.id === task.id ? { ...t, completed: !isPending, ...(status && { status }) } : t
        )),
      }))
      notifications.success("Success")
    } catch (err: any) {
      notifications.error(err?.message || err)
    }
  }

  const { generalTasks, cashbackTasks, firstNotStartedTask } = useMemo((): {
    generalTasks: GameTask[],
    cashbackTasks: GameTask[],
    firstNotStartedTask: GameTask | null
  } => {
    return tasksForPlatform.reduce(
      (acc: { generalTasks: GameTask[], cashbackTasks: GameTask[], firstNotStartedTask: GameTask | null }, task) => {
        if (task.type === "CASHBACK_TASK") {
          acc.cashbackTasks.push(task)
        } else {
          acc.generalTasks.push(task)

          if (!acc.firstNotStartedTask && !task.completed && !isExpired(task)) {
            acc.firstNotStartedTask = task
          }
        }

        return acc
      },
      { generalTasks: [], cashbackTasks: [], firstNotStartedTask: null },
    )
  }, [tasksForPlatform])

  // Aggregator-origin games (gameSource !== NATIVE) support a "Make Task Pending"
  // action; native games don't have the NOT_FOUND status it keys off, so it stays disabled.
  const isAggregatorOrigin = (selectedGame?.gameSource ?? selectedGame?.gameOrigin) !== "NATIVE"

  const generalColumns = [
    ...accountNativeGameBuffPlayColumns,
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => {
        const isDisabledComplete = loading
          || ["CLAIMED", "NOT_CLAIMED"].includes(row.status)
          || row.completed || !selectedGame.isInstalled
          || !!firstNotStartedTask?.sequence && (row.sequence >= firstNotStartedTask?.sequence + 1)
        const isDisabledPending = loading || firstNotStartedTask?.id !== row.id || row.status !== "NOT_FOUND"

        return (
          <div className={Styles.actions}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {isAggregatorOrigin && (
                <Button
                  style={isDisabledPending ? {} : { background: "orange", color: "white" }}
                  disabled={isDisabledPending}
                  onClick={() => onCreateRewardTask(row, "PENDING")}
                >
                  Make Task Pending
                </Button>
              )}
              <Button
                type="primary"
                disabled={isDisabledComplete}
                onClick={() => onCreateRewardTask(row, isAggregatorOrigin ? "NOT_CLAIMED" : undefined)}
              >
                Complete Task
              </Button>
            </div>
          </div>
        )
      },
    },
  ]

  const cashbackColumns = [
    ...accountNativeGameBuffPlayColumns,
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => {
        const isDisabledComplete = loading
          || ["CLAIMED", "NOT_CLAIMED"].includes(row.status)
          || row.completed || !selectedGame.isInstalled
        const isDisabledPending = loading || row.status !== "NOT_FOUND"

        return (
          <div className={Styles.actions}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {isAggregatorOrigin && (
                <Button
                  style={isDisabledPending ? {} : { background: "orange", color: "white" }}
                  disabled={isDisabledPending}
                  onClick={() => onCreateRewardTask(row, "PENDING")}
                >
                  Make Task Pending
                </Button>
              )}
              <Button
                type="primary"
                disabled={isDisabledComplete}
                onClick={() => onCreateRewardTask(row, isAggregatorOrigin ? "NOT_CLAIMED" : undefined)}
              >
                Complete Task
              </Button>
            </div>
          </div>
        )
      },
    },
  ]

  if (!selectedGame) {
    return null
  }

  return (
    <Modal
      open
      onCancel={() => {
        if (loading) return
        accountGamesBuffPlayStore.accountGames = accountGamesBuffPlayStore.accountGames.map(game => (
          game.id === selectedGame.id ? selectedGame : game
        ))
        setSelectedGame(null)
      }}
      width="100%"
      wrapClassName={Styles.modalWrapperNoScroll}
      bodyStyle={{ height: "100vh", padding: 0 }}
      className={Styles.fullscreenModal}
      footer={null}
      title="Update Game"
    >
      <div style={{ display: "flex" }}>
        <div className={Styles.leftPanel}>
          <div className={Styles.imageWrapper}>
            <img src={selectedGame?.bannerUrl} alt="" />
          </div>
          <div style={{ marginBottom: "10px" }}>Name: {selectedGame?.name}</div>
          <div style={{ marginBottom: "20px" }}>Category: {selectedGame?.category}</div>
          <div style={{ marginBottom: "10px" }}>
            <Tabs
              activeKey={activePlatform}
              onChange={key => setActivePlatform(key as GamePlatformType)}
              items={availablePlatforms.map(platform => ({
                key: platform,
                label: PLATFORM_LABELS[platform],
              }))}
            />
          </div>
          <Button
            type="primary"
            style={{ marginBottom: "10px" }}
            disabled={loading || selectedGame.isInstalled}
            onClick={onInstallGame}
          >
            {`Install Game (${PLATFORM_LABELS[activePlatform]})`}
          </Button>
          <Button
            type="primary"
            style={{ marginBottom: "10px" }}
            disabled={loading}
            onClick={onResetGame}
          >
            {`Reset Game (${PLATFORM_LABELS[activePlatform]})`}
          </Button>
          <Button
            type="primary"
            disabled={
              tasksForPlatform.every(t => t.completed)
              || tasksForPlatform.length === 0
              || loading
              || !selectedGame.isInstalled
            }
            style={{ marginBottom: "10px" }}
            onClick={onCompleteGame}
          >
            {`Complete Game (${PLATFORM_LABELS[activePlatform]})`}
          </Button>
        </div>
        <div className={Styles.rightPanel}>
          <Table
            dataSource={generalTasks}
            columns={generalColumns}
            rowKey="id"
            pagination={false}
            loading={accountGamesBuffPlayStore.isLoading}
            onChange={(p, c, sorter: any) => {
              accountGamesBuffPlayStore.setFilter({
                ...accountGamesBuffPlayStore.filter,
                createdAtOrder: sorter.order === "ascend" ? "ASC" : "DESC",
                lastDate: undefined,
              })
              accountGamesBuffPlayStore.loadedPages = 1
            }}
          />
          {cashbackTasks.length > 0 && (
            <>
              <p style={{ marginTop: 20, fontSize: 16, fontWeight: 500 }}>Cashback Tasks</p>
              <Table
                dataSource={cashbackTasks}
                columns={cashbackColumns}
                rowKey="id"
                pagination={false}
                loading={accountGamesBuffPlayStore.isLoading}
              />
            </>
          )}
          {id && selectedGame?.id ? (
            <>
              <AccountDailyMissionsSection accountId={id} gameId={selectedGame.id} platform={activePlatform} />
              <AccountBonusSpendSection accountId={id} gameId={selectedGame.id} platform={activePlatform} />
            </>
          ) : null}
        </div>
      </div>
    </Modal>
  )
})
